import React, { useState, useEffect, useContext, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { Tooltip } from "primereact/tooltip";
import { Dialog } from "primereact/dialog";
import { Calendar } from "primereact/calendar";
import { InputText } from "primereact/inputtext";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { AuthContext } from "../../../../Header/auth - context";
import CustomDropdown from "../../../../custom/customDropdown";

/* Datum se šalje kroz toUtcIso — bez korekcije offseta toISOString ume da
   pomeri dan unazad (vidi CLAUDE.md). */
const toUtcIso = (d) => {
    if (!d) return null;
    const x = d instanceof Date ? d : new Date(d);
    const utc = new Date(x.getTime() - x.getTimezoneOffset() * 60000);
    return utc.toISOString().slice(0, 19) + "Z";
};

const RadniOdnos = () => {
    const { t } = useTranslation();
    const auth = useContext(AuthContext);
    const company = useSelector((state) => state.company);
    /* Zaposleni izabran u Pregledu (ili na drugom tabu) — nosi se kroz sve
       tabove, pa se nudi kao već izabran i ovde. */
    const employee = useSelector((state) => state.employee);
    const dispatch = useDispatch();
    const toast = useRef(null);

    const [redovi, setRedovi] = useState([]);
    const [zaposleniDB, setZaposleniDB] = useState([]);
    const [globalFilter, setGlobalFilter] = useState(null);
    const [ucitava, setUcitava] = useState(false);

    const [prekidRed, setPrekidRed] = useState(null);
    const [datumPrekida, setDatumPrekida] = useState(null);
    const [razlogPrekida, setRazlogPrekida] = useState("");

    const [noviOtvoren, setNoviOtvoren] = useState(false);
    const [noviZaposleni, setNoviZaposleni] = useState(null);
    /* Polja novog perioda — ista ona koja se u UNOS-u ponavljaju za svaki
       radni odnos (odeljak PODACI O RADNOM ODNOSU). Datum otkaza i razlog NE
       idu ovde: oni pripadaju kraju perioda i unose se kroz Prekid. */
    const [nova, setNova] = useState({
        employmentStart: null,
        employmentEnd: null,
        contractType: null,
        education: null,
        position: null,
        additionalPosition: null,
        establishmentDate: null,
        notes: "",
    });
    const [tipoviUgovora, setTipoviUgovora] = useState([]);
    const [pozicije, setPozicije] = useState([]);
    const [strucneSpreme, setStrucneSpreme] = useState([]);

    const izmeni = (polje, vrednost) => setNova((prev) => ({ ...prev, [polje]: vrednost }));

    /* Ugovor na ODREĐENO ima datum do, na neodređeno nema — isto pravilo kao u
       UNOS-u. Prepoznaje se po nazivu, ne po id-u, jer se modgen id-evi po
       instalacijama razlikuju ("Neodređeno" ne počinje na "odre"). */
    const jeOdredjeno = (() => {
        const o = tipoviUgovora.find((x) => x.value === nova.contractType);
        const naziv = String(o?.label || "").replace(/[đĐ]/g, "d");
        return /^odre/i.test(naziv);
    })();

    const poruka = (severity, summary, detail) =>
        toast.current?.show({ severity, summary, detail, life: 4000 });

    const zaglavlje = useCallback(() => ({
        "Content-Type": "application/json",
        Authorization: "Bearer " + auth.token,
    }), [auth.token]);

    const posalji = useCallback(async (step, telo = {}) => {
        const r = await fetch(process.env.REACT_APP_BACKEND_URL + "/employees/radniOdnosi", {
            method: "POST",
            headers: zaglavlje(),
            body: JSON.stringify({ step, ...telo }),
        });
        const data = await r.json();
        if (!r.ok) throw new Error(data.message || t("radniOdnos.greska"));
        return data;
    }, [zaglavlje, t]);

    const ucitaj = useCallback(async () => {
        setUcitava(true);
        try {
            setRedovi(await posalji("GET_SVI"));
        } catch (e) {
            poruka("error", t("common.greska"), e.message);
        } finally {
            setUcitava(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [posalji, t]);

    useEffect(() => { ucitaj(); }, [ucitaj]);

    /* Kandidati za otvaranje radnog odnosa: SAMO oni kojima je radni odnos
       završen (imaju zatvoren period, a nijedan otvoren). Ko je već u radnom
       odnosu nema šta da otvara. */
    useEffect(() => {
        if (!noviOtvoren) return;
        posalji("GET_KANDIDATI")
            .then((r) => setZaposleniDB(r || []))
            .catch(() => setZaposleniDB([]));

        /* Tipovi ugovora i pozicije dolaze iz istog izvora koji koristi UNOS */
        fetch(process.env.REACT_APP_BACKEND_URL + "/employees", {
            method: "POST", headers: zaglavlje(), body: JSON.stringify({}),
        })
            .then((r) => r.json())
            .then((data) => {
                const parsed = JSON.parse(data[0][""]);
                setTipoviUgovora(parsed.TipoviRadnogOdnosa || []);
                setPozicije(parsed.TipoviPozicije || []);
                setStrucneSpreme(parsed.TipoviStrucnaSprema || []);
            })
            .catch(() => { });
    }, [noviOtvoren, posalji, zaglavlje]);

    /* Zajednički izbor upada u polje čim spisak kandidata stigne — samo ako
       ovde još nema izbora i ako je taj zaposleni uopšte kandidat (ko je u
       radnom odnosu nije na spisku). */
    useEffect(() => {
        if (!noviOtvoren || !employee || zaposleniDB.length === 0) return;
        const opcija = zaposleniDB.find((z) => String(z.value) === String(employee));
        if (!opcija) return;
        setNoviZaposleni((prev) => (prev == null ? opcija.value : prev));
    }, [noviOtvoren, employee, zaposleniDB]);

    /* ---- radnje ---- */
    const potvrdiPrekid = async () => {
        if (!datumPrekida) {
            poruka("warn", t("common.upozorenje"), t("radniOdnos.datumObavezan"));
            return;
        }
        try {
            const r = await posalji("PREKID", {
                id: prekidRed.id,
                formData: {
                    terminationDate: toUtcIso(datumPrekida),
                    terminationReason: razlogPrekida || null,
                },
            });
            poruka("success", t("radniOdnos.naslov"), r.message);
            setPrekidRed(null); setDatumPrekida(null); setRazlogPrekida("");
            ucitaj();
        } catch (e) {
            poruka("error", t("common.greska"), e.message);
        }
    };

    /* Period se otvara ovde, u dijalogu — UNOS ostaje netaknut i tamo se posle
       vidi samo poslednji, aktuelni radni odnos. */
    const potvrdiNovi = async () => {
        if (!noviZaposleni || !nova.employmentStart) {
            poruka("warn", t("common.upozorenje"), t("radniOdnos.izaberiOboje"));
            return;
        }
        if (jeOdredjeno && !nova.employmentEnd) {
            poruka("warn", t("common.upozorenje"), t("radniOdnos.krajObavezan"));
            return;
        }
        try {
            const r = await posalji("NOVI", {
                id_zaposleni: noviZaposleni,
                formData: {
                    employmentStart: toUtcIso(nova.employmentStart),
                    /* na neodređeno se datum do ne šalje čak i ako je bio unet
                       pa je tip naknadno promenjen */
                    employmentEnd: jeOdredjeno ? toUtcIso(nova.employmentEnd) : null,
                    establishmentDate: toUtcIso(nova.establishmentDate),
                    contractType: nova.contractType ?? null,
                    education: nova.education ?? null,
                    position: nova.position ?? null,
                    additionalPosition: nova.additionalPosition ?? null,
                    notes: nova.notes || null,
                },
            });
            poruka("success", t("radniOdnos.naslov"), r.message);
            setNoviOtvoren(false);
            setNoviZaposleni(null);
            setNova({
                employmentStart: null, employmentEnd: null, contractType: null,
                education: null, position: null, additionalPosition: null,
                establishmentDate: null, notes: "",
            });
            ucitaj();
        } catch (e) {
            poruka("error", t("common.greska"), e.message);
        }
    };

    const ponistiPrekid = (red) => {
        confirmDialog({
            message: t("radniOdnos.ponistiPitanje", { zaposleni: red.Zaposleni }),
            header: t("radniOdnos.ponistiNaslov"),
            icon: "pi pi-exclamation-triangle",
            acceptLabel: t("common.da"),
            rejectLabel: t("common.ne"),
            accept: async () => {
                try {
                    const r = await posalji("PONISTI_PREKID", { id: red.id });
                    poruka("success", t("radniOdnos.naslov"), r.message);
                    ucitaj();
                } catch (e) {
                    poruka("error", t("common.greska"), e.message);
                }
            },
        });
    };

    const exportExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("RadniOdnosi");
        const headers = ["Zaposleni", "JMBG", "Firma", "Od", "Do", "Period", "Tip ugovora", "Pozicija", "Stanje", "Razlog prekida"];
        const headerRow = worksheet.addRow(headers.map((h) => t(`radniOdnos.k_${h}`, h)));
        headerRow.font = { bold: true };
        headerRow.eachCell((cell) => {
            cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFEFEFEF" } };
            cell.border = { top: { style: "medium" }, left: { style: "medium" }, bottom: { style: "medium" }, right: { style: "medium" } };
        });
        (redovi || []).forEach((r) => {
            const row = worksheet.addRow(headers.map((h) => r[h] ?? ""));
            row.eachCell((cell) => {
                cell.border = { top: { style: "thin" }, left: { style: "thin" }, bottom: { style: "thin" }, right: { style: "thin" } };
            });
        });
        worksheet.columns.forEach((col) => {
            let max = 10;
            col.eachCell((cell) => { if (cell.value) max = Math.max(max, cell.value.toString().length); });
            col.width = max + 2;
        });
        const dt = new Date().toLocaleString("sr-RS", {
            day: "2-digit", month: "2-digit", year: "numeric",
            hour: "2-digit", minute: "2-digit", timeZone: "Europe/Belgrade",
        }).replace(/,/g, "");
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
            `Radni odnosi - ${company?.companyName || ""} - ${dt}.xlsx`);
    };

    const tableHeader = (
        <div className="export-buttons fakture-stavke-header">
            <div style={{ display: "flex", gap: "0.4rem" }}>
                <Button type="button" icon="pi pi-file-excel" severity="success" rounded onClick={exportExcel} data-pr-tooltip="XLS" />
                <Button type="button" icon="pi pi-plus" severity="info" rounded onClick={() => setNoviOtvoren(true)} data-pr-tooltip={t("radniOdnos.novi")} />
                <Button type="button" icon="pi pi-refresh" rounded onClick={ucitaj} data-pr-tooltip={t("radniOdnos.osvezi")} />
            </div>
            <input type="search" placeholder={t("radniOdnos.pretraga")} className="p-inputtext p-component"
                onInput={(e) => setGlobalFilter(e.target.value)} />
        </div>
    );

    return (
        <div className="col-12">
            <Toast ref={toast} />
            <ConfirmDialog />

            <div className="radniOdnosPun">
                <Card>
                    <Tooltip target=".export-buttons>button" position="bottom" />
                    <DataTable
                        value={redovi || []}
                        loading={ucitava}
                        paginator rows={15}
                        globalFilter={globalFilter}
                        scrollable scrollHeight="60vh"
                        header={tableHeader}
                        resizableColumns columnResizeMode="expand"
                        className="turePregledTable stavke-tabela"
                        emptyMessage={t("radniOdnos.nemaPodataka")}
                        /* Prekinut period se izdvaja crvenim redom — ista oznaka
                           koju pregled zaposlenih već koristi za pasivne. */
                        rowClassName={(r) => (r.Otvoren ? "" : "row-invoice-overdue")}
                    >
                        <Column field="Zaposleni" header={t("radniOdnos.k_Zaposleni")} sortable style={{ minWidth: "180px" }} />
                        <Column field="JMBG" header={t("radniOdnos.k_JMBG")} sortable style={{ minWidth: "140px" }} />
                        <Column field="Firma" header={t("radniOdnos.k_Firma")} sortable style={{ minWidth: "160px" }} />
                        <Column field="Od" header={t("radniOdnos.k_Od")} sortable style={{ minWidth: "110px" }} />
                        <Column field="Do" header={t("radniOdnos.k_Do")} sortable style={{ minWidth: "110px" }} />
                        <Column field="Period" header={t("radniOdnos.k_Period")} style={{ minWidth: "90px" }} />
                        <Column field="Tip ugovora" header={t("radniOdnos.k_TipUgovora")} style={{ minWidth: "140px" }} />
                        <Column field="Pozicija" header={t("radniOdnos.k_Pozicija")} style={{ minWidth: "130px" }} />
                        <Column field="Stanje" header={t("radniOdnos.k_Stanje")} sortable style={{ minWidth: "140px" }} />
                        <Column field="Razlog prekida" header={t("radniOdnos.k_Razlog")} style={{ minWidth: "180px" }} />
                        <Column header={t("radniOdnos.k_Radnja")} style={{ minWidth: "150px" }}
                            body={(r) => (r.Otvoren ? (
                                <Button type="button" label={t("radniOdnos.prekid")} icon="pi pi-sign-out"
                                    className="p-button-text p-button-sm p-button-danger"
                                    onClick={() => { setPrekidRed(r); setDatumPrekida(null); setRazlogPrekida(""); }} />
                            ) : (
                                <Button type="button" label={t("radniOdnos.ponisti")} icon="pi pi-undo"
                                    className="p-button-text p-button-sm"
                                    onClick={() => ponistiPrekid(r)} />
                            ))} />
                    </DataTable>
                </Card>
            </div>

            {/* Prekid — datum je obavezan, jer bez njega odlazak nije dokaziv */}
            <Dialog header={t("radniOdnos.prekidNaslov")} visible={!!prekidRed} style={{ width: "640px", maxWidth: "95vw" }}
                onHide={() => setPrekidRed(null)}>
                {prekidRed && (
                    <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                        <div><b>{prekidRed.Zaposleni}</b> — {prekidRed.Firma}</div>
                        <div>
                            <label>{t("radniOdnos.datumPrekida")}: *</label>
                            <Calendar value={datumPrekida} onChange={(e) => setDatumPrekida(e.value)}
                                dateFormat="dd.mm.yy" showIcon style={{ width: "100%" }}
                                minDate={prekidRed.employmentStart ? new Date(prekidRed.employmentStart) : undefined} />
                        </div>
                        <div>
                            <label>{t("radniOdnos.razlog")}:</label>
                            <InputText value={razlogPrekida} onChange={(e) => setRazlogPrekida(e.target.value)}
                                style={{ width: "100%" }} />
                        </div>
                        <Button type="button" label={t("radniOdnos.potvrdiPrekid")} className="p-button-danger"
                            onClick={potvrdiPrekid} />
                    </div>
                )}
            </Dialog>

            {/* Novi period — povratak u firmu ili prelazak iz druge firme kruga */}
            <Dialog header={t("radniOdnos.noviNaslov")} visible={noviOtvoren} style={{ width: "640px", maxWidth: "95vw" }}
                onHide={() => setNoviOtvoren(false)}>
                <div className="radniOdnosForma">
                    <div className="ro-polje ro-puno">
                        <label>{t("radniOdnos.zaposleni")}: *</label>
                        <CustomDropdown style={{ width: "100%" }} options={zaposleniDB} value={noviZaposleni}
                            optionLabel="label" placeholder={t("radniOdnos.izaberiZaposlenog")}
                            onChange={(e) => {
                                /* Izbor ovde menja i zajednički izbor, da ga
                                   ostali tabovi zateknu istog. */
                                dispatch({ type: "EMPLOYEE", value: e.target.value ?? null });
                                setNoviZaposleni(e.target.value);
                            }} />
                    </div>

                    <div className="ro-polje">
                        <label>{t("radniOdnos.k_TipUgovora")}:</label>
                        <CustomDropdown style={{ width: "100%" }} options={tipoviUgovora} value={nova.contractType}
                            optionLabel="label" placeholder={t("radniOdnos.k_TipUgovora")}
                            onChange={(e) => izmeni("contractType", e.target.value)} />
                    </div>
                    <div className="ro-polje">
                        <label>{t("radniOdnos.pocetak")}: *</label>
                        <Calendar value={nova.employmentStart} onChange={(e) => izmeni("employmentStart", e.value)}
                            dateFormat="dd.mm.yy" mask="99.99.9999" showIcon style={{ width: "100%" }} />
                    </div>

                    {/* Datum do postoji samo na ODREĐENO — na neodređeno nema kraja */}
                    {jeOdredjeno && (
                        <div className="ro-polje">
                            <label>{t("radniOdnos.kraj")}: *</label>
                            <Calendar value={nova.employmentEnd} onChange={(e) => izmeni("employmentEnd", e.value)}
                                dateFormat="dd.mm.yy" mask="99.99.9999" showIcon style={{ width: "100%" }}
                                minDate={nova.employmentStart || undefined} />
                        </div>
                    )}

                    <div className="ro-polje">
                        <label>{t("radniOdnos.datumZasnivanja")}:</label>
                        <Calendar value={nova.establishmentDate} onChange={(e) => izmeni("establishmentDate", e.value)}
                            dateFormat="dd.mm.yy" mask="99.99.9999" showIcon style={{ width: "100%" }} />
                    </div>
                    <div className="ro-polje">
                        <label>{t("radniOdnos.k_Pozicija")}:</label>
                        <CustomDropdown style={{ width: "100%" }} options={pozicije} value={nova.position}
                            optionLabel="label" placeholder={t("radniOdnos.k_Pozicija")}
                            onChange={(e) => izmeni("position", e.target.value)} />
                    </div>
                    <div className="ro-polje">
                        <label>{t("radniOdnos.dodatnaFunkcija")}:</label>
                        <CustomDropdown style={{ width: "100%" }} options={pozicije} value={nova.additionalPosition}
                            optionLabel="label" placeholder={t("radniOdnos.dodatnaFunkcija")}
                            onChange={(e) => izmeni("additionalPosition", e.target.value)} />
                    </div>
                    <div className="ro-polje">
                        <label>{t("radniOdnos.strucnaSprema")}:</label>
                        <CustomDropdown style={{ width: "100%" }} options={strucneSpreme} value={nova.education}
                            optionLabel="label" placeholder={t("radniOdnos.strucnaSprema")}
                            onChange={(e) => izmeni("education", e.target.value)} />
                    </div>
                    <div className="ro-polje ro-puno">
                        <label>{t("radniOdnos.napomena")}:</label>
                        <InputText value={nova.notes} onChange={(e) => izmeni("notes", e.target.value)}
                            style={{ width: "100%" }} />
                    </div>

                    <small className="ro-puno" style={{ color: "#6c757d" }}>{t("radniOdnos.noviNapomena")}</small>
                    <Button type="button" label={t("radniOdnos.potvrdiNovi")} className="p-button-success ro-puno"
                        onClick={potvrdiNovi} />
                </div>
            </Dialog>
        </div>
    );
};

export default RadniOdnos;
